// apps/client/src/ui/screens/writer.tsx (M1-T17) — write a letter: a recipient (fixed from their mailbox, or chips when the Menu
// opened it with no params, Contract change 37), the body with a live count, one gift from pockets, optionally wrapped. Stationery and
// wrapping are taken from pockets by sendLetter; without stationery Send is replaced by a hint.
import { getEntry, tuning, type CatalogId } from '@whiskerton/content';
import type { ItemId, ItemView, ResidentId } from '@whiskerton/shared';
import { useId, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getGame, useCommands } from '../../game/instance';
import { sendLetter } from '../../state/slices/mail';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Button, Panel, Tag } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { writer: { to: ResidentId | null } | undefined } }

const kindOf = (i: ItemView) => getEntry(i.catalogId)?.kind;
const pressed = (on: boolean) => ({ outline: on ? '3px solid var(--leaf-700)' : 'none', outlineOffset: 2 });

function GiftButton({ item, on, pick }: { item: ItemView; on: boolean; pick: () => void }) {
  const t = useT();
  const icon = useIcon(item.catalogId as CatalogId);
  const entry = getEntry(item.catalogId);
  return (
    <button type="button" className="slot" aria-pressed={on} onClick={pick} style={{ ...pressed(on), display: 'grid', gap: 2, aspectRatio: 'auto', padding: 6, font: 'inherit', fontWeight: 800, fontSize: 13 }}>
      <img src={icon} alt="" style={{ width: 40, height: 40, objectFit: 'contain' }} />
      <span>{entry ? itemName(entry.name) : item.catalogId}{item.qty > 1 && ` ${t('items.qty', { n: item.qty })}`}</span>
    </button>
  );
}

function Writer({ params, close }: { params: { to: ResidentId | null } | undefined; close: () => void }) {
  const t = useT();
  const cmd = useCommands();
  const bodyId = useId();
  const { you, residents } = useGame(useShallow((s) => ({ you: s.you, residents: s.residents })));
  const fixed = params?.to ?? null;
  const [to, setTo] = useState<ResidentId | null>(fixed);
  const [body, setBody] = useState('');
  const [gift, setGift] = useState<ItemId | null>(null);
  const [wrap, setWrap] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<'mail.sentNow' | 'mail.sentRound' | null>(null);

  const pockets = (you?.items ?? []).filter((i) => i.at.loc === 'pockets');
  const hasStationery = pockets.some((i) => kindOf(i) === 'stationery');
  const hasWrapping = pockets.some((i) => kindOf(i) === 'wrapping');
  const gifts = pockets.filter((i) => kindOf(i) !== 'stationery' && kindOf(i) !== 'wrapping');
  const picked = gifts.find((g) => g.id === gift) ?? null;               // a stack goes whole: mail.send moves the item row
  const others = Object.values(residents).filter((r) => r.id !== you?.resident);
  const recipient = to ? residents[to] : undefined;
  const max = tuning.mail.maxChars;
  const tooLong = body.length > max;
  const canSend = !!to && !busy && !tooLong && (body.trim().length > 0 || gift !== null);

  const send = async () => {
    if (!to) return;
    setBusy(true);
    try {
      const res = await sendLetter(cmd.act, pockets, { to, body, gift: gift && gifts.some((g) => g.id === gift) ? gift : null, wrap });
      if (!res.ok) return;                                       // rejects are toasted by the action client
      const now = getGame().socket?.clock.now() ?? Date.now();
      setSent(res.r.deliverAt <= now ? 'mail.sentNow' : 'mail.sentRound');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <Panel title={t('mail.writeTitle')}>
        <div style={{ display: 'grid', gap: 12, width: 'min(420px, 70cqw)', maxWidth: '100%' }}>
          <p style={{ margin: 0, fontWeight: 800 }}>{t(sent)}</p>
          <Button onClick={close}>{t('common.ok')}</Button>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title={t('mail.writeTitle')}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '2.4cqw', width: '78cqw', maxWidth: '100%' }}>
        <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 800 }}>{t('mail.to')}</span>
            {fixed && recipient && <Tag color={recipient.color}>{recipient.name}</Tag>}
            {!fixed && others.length === 0 && <span>{t('mail.pickRecipient')}</span>}
          </div>
          {!fixed && others.length > 0 && (
            <div role="group" aria-label={t('mail.pickRecipient')} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {others.map((r) => (
                <button key={r.id} type="button" aria-pressed={to === r.id} onClick={() => setTo(r.id)}
                  style={{ ...pressed(to === r.id), border: 0, background: 'none', padding: 0, cursor: 'pointer', borderRadius: 999, font: 'inherit' }}>
                  <Tag color={r.color}>{r.name}</Tag>
                </button>
              ))}
            </div>
          )}
          <label className="field" htmlFor={bodyId}>
            {t('mail.body')}
            <textarea id={bodyId} value={body} rows={6} onChange={(e) => setBody(e.target.value)}
              style={{ font: 'inherit', fontWeight: 800, fontSize: 17, lineHeight: 1.45, color: 'var(--wood-800)', background: 'var(--cream-100)', border: 0,
                borderRadius: 14, boxShadow: 'var(--sh-well)', padding: '12px 14px', resize: 'none' }} />
          </label>
          <span style={{ justifySelf: 'end', fontSize: 14, fontWeight: 800, color: tooLong ? 'var(--flag-500)' : 'var(--wood-600)' }}>
            {t('mail.count', { n: body.length, max })}
          </span>
        </div>
        <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          <span style={{ fontWeight: 800 }}>{t('mail.attach')}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <button type="button" className="slot" aria-pressed={gift === null} onClick={() => { setGift(null); setWrap(false); }}
              style={{ ...pressed(gift === null), aspectRatio: 'auto', padding: 6, font: 'inherit', fontWeight: 800, fontSize: 13 }}>{t('mail.noGift')}</button>
            {gifts.map((i) => <GiftButton key={i.id} item={i} on={gift === i.id} pick={() => setGift(i.id)} />)}
          </div>
          {picked && picked.qty > 1 && <p style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>{t('mail.giftStack', { n: picked.qty })}</p>}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, opacity: hasWrapping && gift ? 1 : 0.6 }}>
            <input type="checkbox" checked={wrap && !!gift} disabled={!hasWrapping || !gift} onChange={(e) => setWrap(e.target.checked)} />
            {t('mail.wrap')}
          </label>
          {!hasWrapping && <p style={{ margin: 0, fontSize: 14 }}>{t('mail.needWrapping')}</p>}
          {hasStationery
            ? <Button disabled={!canSend} onClick={() => void send()}>{t('mail.send')}</Button>
            : <p style={{ margin: 0, fontWeight: 800 }}>{t('mail.needStationery')}</p>}
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'writer'> = {
  id: 'writer', layer: 'panel', menu: { label: 'mail.writeTitle', order: 11 },
  component: ({ params, close }) => <Writer params={params} close={close} />,
};
